/**
 * Shared lead-form validation rules.
 *
 * This module is imported by the browser forms (for UX) and by the
 * Netlify function (for enforcement). Client-side results are never
 * trusted: every rule here is re-run server-side before a lead is
 * forwarded to the CRM.
 */

// Name of the honeypot input. Any value in it means a bot filled the form.
export const HONEYPOT_FIELD = "contact_ref_8f3"

export const MAX_PAYLOAD_BYTES = 15000

export const LIMITS = {
  name: { min: 2, max: 80 },
  email: { max: 254 },
  comment: { max: 2000 },
}

export const normalizeText = (value = "") =>
  String(value)
    .normalize("NFKC")
    // strip control characters bots use to smuggle payloads past filters
    .replace(/[\u0000-\u001F\u007F]/g, "")
    .trim()

// Matches a whole link once, so "http://a.com" counts as one URL rather than
// matching the scheme and the domain separately.
const URL_PATTERN = /(?:https?:\/\/|www\.)[^\s]+|[a-z0-9-]+\.(?:com|net|org|ru|cn|io|info|biz|xyz|top|club|online|site)\b/gi

// Ten or more of the same character in a row ("aaaaaaaaaa"). Real names and
// US addresses never do this, but spam submissions frequently do.
const REPEATED_CHAR_PATTERN = /(.)\1{9,}/

export const countUrls = (value = "") => {
  const matches = normalizeText(value).match(URL_PATTERN)
  return matches ? matches.length : 0
}

export const hasUrl = (value = "") => countUrls(value) > 0

export const hasExcessiveRepeats = (value = "") =>
  REPEATED_CHAR_PATTERN.test(normalizeText(value))

// Runs of keys adjacent on a QWERTY keyboard, plus the filler strings bots
// reuse. A real word never contains these sequences.
const KEYBOARD_RUN_PATTERN = /asdf|qwer|zxcv|hjkl|testtest|abcabc/i

// Five or more of the same character in a row ("aaaaa"). Tighter than
// REPEATED_CHAR_PATTERN, which stays at ten for the fields that allow it.
const GIBBERISH_REPEAT_PATTERN = /(.)\1{4,}/

// Only judge a word long enough for the ratio to mean something. Short real
// surnames (Ng, Smyth) would otherwise look consonant-heavy by chance.
const MIN_RATIO_WORD_LENGTH = 12
const MIN_VOWEL_RATIO = 0.15

/**
 * Detects keyboard-mashed input like "sadfhalkshdfjlkahsdfj".
 *
 * Deliberately conservative: on a lead form a rejected real customer costs
 * far more than an accepted spam message, so every rule here is tuned to
 * pass unusual-but-real values. In particular `y` counts as a vowel, or
 * Smyth, Rhythm and Krzysztof would all be rejected.
 *
 * Not applied to addresses, company names, building models, zips or phone
 * numbers, whose legitimate values often look random by these measures.
 */
export const isGibberish = (value = "") => {
  const text = normalizeText(value).toLowerCase()

  // Too short to judge: "Hi", "Bob", "Ng" must pass.
  if (text.length < 5) return false

  if (GIBBERISH_REPEAT_PATTERN.test(text)) return true
  if (KEYBOARD_RUN_PATTERN.test(text)) return true

  const words = text.split(/\s+/)

  // Consonant ratio, measured per word rather than across the whole string
  // so one long mashed word is not diluted by surrounding real ones. Every
  // long word must fail before the value is called gibberish.
  const longWords = words.filter(
    word => word.replace(/[^a-z]/g, "").length >= MIN_RATIO_WORD_LENGTH
  )
  if (
    longWords.length > 0 &&
    longWords.every(word => {
      const letters = word.replace(/[^a-z]/g, "")
      const vowels = letters.match(/[aeiouy]/g) || []
      return vowels.length / letters.length < MIN_VOWEL_RATIO
    })
  )
    return true

  // A very long word with no vowel at all is never a real word.
  if (words.some(word => word.length > 25 && !/[aeiouy]/i.test(word)))
    return true

  return false
}

/**
 * Gibberish check for an email: only the local part is examined, because a
 * domain is outside the sender's control and often abbreviated.
 */
export const hasGibberishEmailUser = (value = "") => {
  const email = normalizeText(value)
  const at = email.indexOf("@")
  if (at <= 0) return false
  return isGibberish(email.slice(0, at))
}

// Digits only, so (704) 555-1234 and 704-555-1234 both pass.
export const digitsOnly = (value = "") => String(value).replace(/\D/g, "")

export const isValidEmail = (value = "") => {
  const email = normalizeText(value)
  if (!email || email.length > LIMITS.email.max) return false
  // Deliberately permissive: one @, no spaces, a dotted domain.
  return /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/.test(email)
}

export const isValidPhone = (value = "") => {
  const digits = digitsOnly(value)
  return digits.length >= 10 && digits.length <= 11
}

export const isValidZip = (value = "") =>
  /^\d{5}(-\d{4})?$/.test(normalizeText(value))

/**
 * Validates a name field. Intentionally lenient so real US customer and
 * business names pass: accented characters, hyphens, apostrophes,
 * periods and multi-word names are all allowed.
 */
export const validateName = (value = "", label = "Name") => {
  const name = normalizeText(value)
  if (!name) return `${label} is required.`
  if (name.length < LIMITS.name.min)
    return `${label} must be at least ${LIMITS.name.min} characters.`
  if (name.length > LIMITS.name.max)
    return `${label} must be under ${LIMITS.name.max} characters.`
  if (hasUrl(name)) return `${label} cannot contain a web address.`
  if (hasExcessiveRepeats(name) || isGibberish(name))
    return `Please enter a valid ${label.toLowerCase()}.`
  return ""
}

export const validateEmail = (value = "") => {
  const email = normalizeText(value)
  if (!email) return "Email is required."
  if (!isValidEmail(email)) return "Please enter a valid email address."
  // Local part only: the domain is not the sender's to choose.
  if (hasGibberishEmailUser(email)) return "Please enter a valid email address."
  return ""
}

export const validatePhone = (value = "") => {
  const phone = normalizeText(value)
  if (!phone) return "Phone number is required."
  if (!isValidPhone(phone)) return "Please enter a valid 10-digit phone number."
  return ""
}

export const validateZip = (value = "") => {
  const zip = normalizeText(value)
  if (!zip) return "Zip code is required."
  if (!isValidZip(zip))
    return "Please enter a valid zip code (12345 or 12345-6789)."
  return ""
}

export const validateRequired = (value = "", label = "This field") => {
  if (!normalizeText(value)) return `${label} is required.`
  return ""
}

export const validateComment = (
  value = "",
  label = "Message",
  required = true
) => {
  const comment = normalizeText(value)
  if (!comment) return required ? `${label} is required.` : ""
  if (comment.length > LIMITS.comment.max)
    return `${label} must be under ${LIMITS.comment.max} characters.`
  if (countUrls(comment) >= 3) return `${label} cannot contain multiple links.`
  if (hasExcessiveRepeats(comment) || isGibberish(comment))
    return `Please enter a valid ${label.toLowerCase()}.`
  return ""
}

/**
 * Runs the rules that apply to a given set of fields and returns a map of
 * { field: errorMessage } containing only the fields that failed.
 *
 * `fields` holds the raw form values; only keys present are checked, so the
 * same function serves forms with different field sets.
 */
export const validateLead = (fields = {}) => {
  const errors = {}

  const check = (key, message) => {
    if (message) errors[key] = message
  }

  if ("first_name" in fields)
    check("first_name", validateName(fields.first_name, "Name"))
  if ("last_name" in fields)
    check("last_name", validateName(fields.last_name, "Last name"))
  if ("email" in fields) check("email", validateEmail(fields.email))

  // Phone arrives as mobile_no on some forms and phone_no on others.
  if ("mobile_no" in fields) check("mobile_no", validatePhone(fields.mobile_no))
  if ("phone_no" in fields) check("phone_no", validatePhone(fields.phone_no))

  // Zip likewise varies between zip and zipcode.
  if ("zip" in fields) check("zip", validateZip(fields.zip))
  if ("zipcode" in fields) check("zipcode", validateZip(fields.zipcode))

  if ("state" in fields) check("state", validateRequired(fields.state, "State"))
  if ("city" in fields) check("city", validateRequired(fields.city, "City"))
  if ("building_type" in fields)
    check(
      "building_type",
      validateRequired(fields.building_type, "Building type")
    )
  if ("comment" in fields) check("comment", validateComment(fields.comment))

  return errors
}

export const hasErrors = errors => Object.keys(errors).length > 0
